import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Grid, Stack, Card, Backdrop, IconButton, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Loader from "components/loader";
import AlertMessage from "components/UI/AlertMessage";
import useAlert from "hooks/useAlert";
import useAPI from "hooks/useAPI";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import Unauthorized from "components/UI/Unauthorized";
import { TableHeading, TableStatusCell } from "components/TableComponents";
import PlusIcon from "Icons/svg/PlusIcon";
import WithPermission from "components/WithPermission";
import { Edit, Delete } from "@mui/icons-material";
import MBConfirmationPopup from "components/MBConfirmationPopup";
import ManufacturerFormPopup from "./ManufacturerFormPopup";
import { useTranslation } from "react-i18next";
import { formatDateWithFormat } from "utils/utilities";
import SortIcon from "components/SortIcon";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import { Refresh, SettingsOutlined } from "@mui/icons-material";

const Manufacturers = () => {
  const { t } = useTranslation();
  const { per_page, date_time_format, items_per_page_options } = useConstants();

  const manufacturers_table_id = "manufacturers";
  const default_sort_column = "id";
  const default_sort_direction = "asc";
  const default_items_per_page = per_page;

  const { errorState, showAlert } = useAlert();
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(default_items_per_page);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] =
    useState(default_items_per_page);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [tableId, setTableId] = useState(null);
  const { api } = useAPI();
  const { hasPermission } = usePermissions();
  const [backdrop, setBackdrop] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [popupMode, setPopupMode] = useState("add");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [manufacturerToDelete, setManufacturerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  if (!hasPermission("can_view_manufacturers")) {
    return <Unauthorized />;
  }

  const queryParams = new URLSearchParams(location.search);
  const param_dev = queryParams.get("dev");
  const isDev = param_dev === "true" || param_dev === "1";

  const fetchManufacturers = async (
    sortCol = null,
    sortDir = null,
    pageSize = null,
    isReset = false,
    searchValue = null,
    nextPage = null
  ) => {
    setLoading(true);
    let requestConfig = {};
    if (!isInitialLoad && !isReset) {
      const params = {
        page: nextPage || page,
        per_page: pageSize || perPage,
        search: searchValue,
        order_col: sortCol || orderColumn,
        order_dir: sortDir || orderDirection,
      };
      requestConfig = { params };
    }
    try {
      const response = await api.get("/manufacturers/", requestConfig);
      if (response.status !== 200) {
        showAlert("error", response.data.error);
        setLoading(false);
        return;
      }
      const result = response.data;
      const page_settings = result.page_settings;
      setManufacturers(result.data);
      setTotal(result.recordsTotal);

      if (isInitialLoad || isReset) {
        if (page_settings && page_settings.sortColumn) {
          setOrderColumn(page_settings.sortColumn);
          setDefaultSortColumnFromSettings(page_settings.sortColumn);
        }
        if (page_settings && page_settings.sortDirection) {
          setOrderDirection(page_settings.sortDirection);
          setDefaultSortDirectionFromSettings(page_settings.sortDirection);
        }
        if (page_settings && page_settings.itemsPerPage) {
          setPerPage(page_settings.itemsPerPage);
          setDefaultItemsPerPageFromSettings(page_settings.itemsPerPage);
        }
        if (isReset && (!page_settings || Object.keys(page_settings).length === 0)) {
          setOrderColumn(default_sort_column);
          setOrderDirection(default_sort_direction);
          setPerPage(default_items_per_page);
          setDefaultSortColumnFromSettings(default_sort_column);
          setDefaultSortDirectionFromSettings(default_sort_direction);
          setDefaultItemsPerPageFromSettings(default_items_per_page);
        }
        setIsInitialLoad(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching manufacturers:", error); // Debug log
      setError(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturers(orderColumn, orderDirection, perPage, false, search, page);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchManufacturers(orderColumn, orderDirection, perPage, false, search, newPage);
  };
  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
    fetchManufacturers(orderColumn, orderDirection, newPerPage, false, search, page);
  };
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
    fetchManufacturers(orderColumn, orderDirection, perPage, false, event.target.value, page);
  };
  const handleSort = (column, sortDirection) => {
    const newSortColumn = column.columnKey;
    let newSortDirection = sortDirection;
    if (newSortColumn === orderColumn) {
      newSortDirection = orderDirection === "asc" ? "desc" : "asc";
    }
    setOrderColumn(newSortColumn);
    setOrderDirection(newSortDirection);
    fetchManufacturers(newSortColumn, newSortDirection, perPage, false, search, page);
  };

  const handleAddManufacturer = () => {
    setSelectedManufacturer(null);
    setPopupMode("add");
    setShowPopup(true);
  };

  const handleEditManufacturer = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setPopupMode("edit");
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedManufacturer(null);
  };

  const handleFormSuccess = () => {
    // Refresh the manufacturers list
    fetchManufacturers(orderColumn, orderDirection, perPage, false, search, page);
  };

  const handleDeleteManufacturer = (manufacturer) => {
    setManufacturerToDelete(manufacturer);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!manufacturerToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/manufacturers/${manufacturerToDelete.id}/`);
      showAlert("success", t("lang.manufacturer_deleted_successfully"));

      // Refresh the manufacturers list
      fetchManufacturers(orderColumn, orderDirection, perPage, false, search, page);

      // Close confirmation popup
      setShowDeleteConfirmation(false);
      setManufacturerToDelete(null);
    } catch (error) {
      console.error("Error deleting manufacturer:", error);

      if (error.response?.data?.message) {
        showAlert("error", error.response.data.message);
      } else {
        showAlert("error", t("lang.failed_to_delete_manufacturer"));
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setManufacturerToDelete(null);
  };

  const handleSettingsClick = (tableId) => {
    setTableId(tableId);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const newSettings = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const response = await api.post(`/user-page-settings/`, {
        page_slug: "manufacturers",
        settings: settings,
      });
      if (response.status == 200) {
        // Apply the new settings
        setOrderColumn(newSettings.sortColumn);
        setOrderDirection(newSettings.sortDirection);
        setPerPage(newSettings.itemsPerPage);
        setDefaultSortColumnFromSettings(newSettings.sortColumn);
        setDefaultSortDirectionFromSettings(newSettings.sortDirection);
        setDefaultItemsPerPageFromSettings(newSettings.itemsPerPage);
        setPage(1); // Reset to first page

        // Fetch data with new settings
        fetchManufacturers(
          newSettings.sortColumn,
          newSettings.sortDirection,
          newSettings.itemsPerPage,
          false,
          search,
          1
        );
        setOpenSettingsDrawer(false);
        setTableId(null);

        showAlert("success", response.data.message);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  const handleResetTable = () => {
    setSearch("");
    fetchManufacturers(orderColumn, orderDirection, perPage, true, "", 1);
  };

  const getSortIcon = (columnKey) => {
    const isActive = orderColumn === columnKey;
    return <SortIcon orderDirection={orderDirection} isActive={isActive} />;
  };

  const columns = [
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.id")} />
          {getSortIcon("id")}
        </SoftBox>
      ),
      columnKey: "id",
      selector: (row) => row.id,
      sortable: true,
      width: "7%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.name")} />
          {getSortIcon("name")}
        </SoftBox>
      ),
      columnKey: "name",
      selector: (row) => row.name,
      sortable: true,
      width: "30%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.created_at")} />
          {getSortIcon("created_at")}
        </SoftBox>
      ),
      columnKey: "created_at",
      selector: (row) => formatDateWithFormat(row.created_at, date_time_format),
      sortable: true,
      //   width: "30%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.actions.action")} />
        </SoftBox>
      ),
      columnKey: "actions",
      sortable: false,
      width: "15%",
      right: true,
      cell: (row) => (
        <SoftBox display="flex" gap={1}>
          <WithPermission permission="can_edit_manufacturers">
            <Tooltip title={t("lang.actions.edit")} placement="top">
              <IconButton size="small" color="dark" onClick={() => handleEditManufacturer(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </WithPermission>
          {isDev && (
            <WithPermission permission="can_delete_manufacturers">
              <Tooltip title={t("lang.actions.delete")} placement="top">
                <IconButton size="small" color="dark" onClick={() => handleDeleteManufacturer(row)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </WithPermission>
          )}
        </SoftBox>
      ),
    },
  ];

  const pagesList = [
    {
      name: t("lang.manufacturers"),
      route: "",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />
      <SoftBox pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="flex-end" m={2}>
              <WithPermission permission="can_add_manufacturers">
                <SoftButton variant="contained" color="dark" onClick={handleAddManufacturer}>
                  <PlusIcon />
                </SoftButton>
              </WithPermission>
            </SoftBox>
            <Card>
              <SoftBox p={2} position="relative">
                <Backdrop
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 253, 253, 0.71)",
                    zIndex: 1,
                  }}
                  open={backdrop}
                >
                  <Loader color="inherit" />
                </Backdrop>
                <SoftBox
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: {
                      xs: "100%", // Full width on extra-small screens (mobile)
                      sm: "12rem", // Fixed width on small and up
                      md: "20rem",
                    },
                    ml: "auto",
                    mb: "1rem",
                  }}
                >
                  <Tooltip title={t("lang.settings")} arrow placement="top">
                    <SoftButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleSettingsClick(manufacturers_table_id)}
                      size="small"
                      sx={{
                        padding: "0.45rem 1rem",
                        "& svg": {
                          fontSize: "24px !important",
                        },
                      }}
                    >
                      <SettingsOutlined color="secondary" />
                    </SoftButton>
                  </Tooltip>
                  <Tooltip title={t("lang.reset")} arrow placement="top">
                    <SoftButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleResetTable()}
                      size="small"
                      sx={{
                        padding: "0.45rem 1rem",
                        "& svg": {
                          fontSize: "24px !important",
                        },
                      }}
                    >
                      <Refresh color="secondary" />
                    </SoftButton>
                  </Tooltip>
                  <SoftBox width="12rem" ml="auto">
                    <SoftInput
                      placeholder={t("lang.actions.search")}
                      fullWidth
                      value={search}
                      onChange={handleSearchChange}
                    />
                  </SoftBox>
                </SoftBox>
                <DataTable
                  className={manufacturers_table_id}
                  key={`${orderColumn}-${orderDirection}-${perPage}-${manufacturers.length}`}
                  columns={columns}
                  data={Array.isArray(manufacturers) ? manufacturers : []}
                  progressPending={loading}
                  progressComponent={<Loader />}
                  pagination
                  paginationServer
                  paginationTotalRows={total}
                  onChangeRowsPerPage={handlePerPageChange}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  sortServer
                  paginationPerPage={perPage}
                  paginationRowsPerPageOptions={items_per_page_options}
                  highlightOnHover
                  paginationDefaultPage={page}
                  clearSelectedRows={true}
                  responsive={true}
                  selectableRows={false}
                  pointerOnHover={false}
                  fixedHeader={true}
                  persistTableHead={true}
                  sortIcon={() => {
                    return <></>;
                  }}
                />
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <AlertMessage {...errorState} />

      <ManufacturerFormPopup
        open={showPopup}
        onClose={handleClosePopup}
        onSuccess={handleFormSuccess}
        manufacturer={selectedManufacturer}
        mode={popupMode}
        showAlert={showAlert}
      />

      <MBConfirmationPopup
        open={showDeleteConfirmation}
        handleConfirm={handleDeleteConfirm}
        handleClose={handleDeleteCancel}
        btnLoading={deleting}
        title={t("lang.delete_manufacturer")}
        message={
          manufacturerToDelete
            ? t("lang.are_you_sure_to_delete_this_manufacturer", {
                name: manufacturerToDelete.name,
              })
            : t("lang.are_you_sure_to_delete_manufacturer")
        }
        confirmText={t("lang.actions.delete")}
        cancelText={t("lang.actions.cancel")}
      />

      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings")}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") },
            { value: "name", label: t("lang.name") },
            { value: "created_at", label: t("lang.created_at") },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
    </DashboardLayout>
  );
};

export default Manufacturers;
